/**
 * Resolves external source metadata for chat → document clips (e.g. Threads).
 * Prefer explicit URL query params, then thread topic/title text, then page URL.
 */

export type ChatClipSourceInput = {
  searchString: string;
  /** Normalized to string | null inside `computeChatClipSource` */
  threadId: string | null | undefined;
  threadTitle: string | null;
  threadTopic: string | null;
  pageHref: string;
};

function coerceThreadId(raw: string | null | undefined | unknown): string | null {
  if (raw == null || raw === "") return null;
  if (typeof raw === "string") {
    const t = raw.trim();
    return t || null;
  }
  if (typeof raw === "number" && Number.isFinite(raw)) {
    const t = String(raw).trim();
    return t || null;
  }
  if (typeof raw === "boolean") return null;
  try {
    const t = String(raw).trim();
    return t || null;
  } catch {
    return null;
  }
}

export type ChatClipSourceResolved = {
  threadId: string | null;
  sourceUrl: string | null;
  sourceLabel: string | null;
};

const PARAM_KEYS = ["sourceUrl", "source_url", "externalUrl", "threadsUrl", "originUrl"] as const;
const LABEL_KEYS = ["sourceLabel", "source_label", "sourceTitle", "source_title"] as const;

const URL_IN_TEXT = /(https?:\/\/[^\s<'")\]]+)/gi;

function pickParam(params: URLSearchParams, keys: readonly string[]): string | null {
  for (const k of keys) {
    const v = params.get(k);
    if (v && v.trim()) {
      try {
        return decodeURIComponent(v.trim());
      } catch {
        return v.trim();
      }
    }
  }
  return null;
}

export function isLikelyExternalThreadSourceUrl(raw: string): boolean {
  try {
    const u = new URL(raw);
    const h = u.hostname.replace(/^www\./, "").toLowerCase();
    if (h === "threads.net") return true;
    if (h === "instagram.com" || h === "l.instagram.com") return true;
    return false;
  } catch {
    return false;
  }
}

function extractFirstSourceUrlFromText(text: string | null | undefined): string | null {
  if (!text) return null;
  URL_IN_TEXT.lastIndex = 0;
  let m: RegExpExecArray | null;
  while ((m = URL_IN_TEXT.exec(text)) !== null) {
    if (isLikelyExternalThreadSourceUrl(m[1])) return m[1];
  }
  return null;
}

function inferLabelFromThreadsUrl(url: string): string | null {
  try {
    const u = new URL(url);
    const parts = u.pathname.split("/").filter(Boolean);
    const at = parts.find((p) => p.startsWith("@"));
    if (at) return at;
    return u.hostname.replace(/^www\./, "");
  } catch {
    return null;
  }
}

function isAppChatThreadPermalink(url: string): boolean {
  try {
    const u = new URL(url);
    return u.pathname.replace(/\/+$/, "") === "/chat" && u.searchParams.has("threadId");
  } catch {
    return false;
  }
}

export function inferSourceLabel(
  sourceUrl: string | null,
  explicitLabel: string | null,
  threadTitle: string | null
): string | null {
  const ex = explicitLabel?.trim();
  if (ex) return ex;
  if (sourceUrl) {
    const fromUrl = inferLabelFromThreadsUrl(sourceUrl);
    if (fromUrl) return fromUrl;
    if (isAppChatThreadPermalink(sourceUrl)) {
      const t = threadTitle?.trim();
      if (t) return t.length > 80 ? `${t.slice(0, 77)}…` : t;
      return "Chat thread";
    }
  }
  const t = threadTitle?.trim();
  if (t) return t.length > 80 ? `${t.slice(0, 77)}…` : t;
  return sourceUrl ? "Source" : null;
}

export function computeChatClipSource(input: ChatClipSourceInput): ChatClipSourceResolved {
  const q = input.searchString?.startsWith("?") ? input.searchString.slice(1) : input.searchString || "";
  const params = new URLSearchParams(q);

  const explicitParamUrl = pickParam(params, PARAM_KEYS);
  let sourceUrl =
    explicitParamUrl ||
    extractFirstSourceUrlFromText(input.threadTopic) ||
    extractFirstSourceUrlFromText(input.threadTitle);

  if (!sourceUrl && input.pageHref) {
    try {
      const page = new URL(input.pageHref);
      const full = page.toString();
      if (isLikelyExternalThreadSourceUrl(full)) {
        sourceUrl = `${page.origin}${page.pathname}`;
      }
    } catch {
      /* ignore */
    }
  }

  const fromExplicitParam = !!explicitParamUrl;
  if (sourceUrl && fromExplicitParam && !isLikelyExternalThreadSourceUrl(sourceUrl)) {
    sourceUrl = null;
  }

  const threadId = coerceThreadId(input.threadId);

  // Persisted thread without an external Threads URL: still link clips to this conversation.
  if (!sourceUrl && threadId) {
    try {
      const base = new URL(input.pageHref || "http://localhost");
      sourceUrl = `${base.origin}/chat?threadId=${encodeURIComponent(threadId)}`;
    } catch {
      sourceUrl = null;
    }
  }

  const explicitLabel = pickParam(params, LABEL_KEYS);
  const sourceLabel = inferSourceLabel(sourceUrl, explicitLabel, input.threadTitle);

  return {
    threadId,
    sourceUrl,
    sourceLabel,
  };
}
